import { useSyncExternalStore } from "react";
import { readToken, logout, USER_STORAGE_KEY } from "@/api/client";

export interface UsuarioLogado {
  usuarioId: number;
  nome: string;
  email?: string;
  roles: string[];
  instituicao?: { id: number; codigo?: string; nome?: string };
}

// Cache por string bruta: useSyncExternalStore compara a snapshot por
// Object.is() a cada render. Se readUsuario() desse um JSON.parse() novo toda
// vez, cada render veria uma referência diferente e o React entenderia que a
// store "está sempre mudando" — loop de re-render. Só reprocessa o JSON quando a
// string do localStorage muda de fato.
let cachedRaw: string | null | undefined;
let cachedValue: UsuarioLogado | null = null;

function readUsuarioCached(): UsuarioLogado | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    try {
      cachedValue = raw ? JSON.parse(raw) : null;
    } catch {
      cachedValue = null;
    }
  }
  return cachedValue;
}

// O evento "storage" só dispara em OUTRAS abas/contextos de navegação, nunca na
// aba que fez a escrita — é exatamente a semântica que queremos aqui: quando o
// Angular (outra aba, ou a mesma origem após um full reload) renova o token ou
// desloga, esta aba React observa de graça, sem polling.
function subscribeToStorage(callback: () => void): () => void {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

export function useAuth() {
  const usuario = useSyncExternalStore(subscribeToStorage, readUsuarioCached);

  return {
    usuario,
    isAuthenticated: !!readToken()?.access_token && !!usuario,
    hasRole: (role: string) => !!usuario?.roles?.includes(role),
    logout,
  };
}
